export type FieldFormat = 'ssn' | 'address' | 'currency';

export type FieldDef = {
  label: string;
  // Labels as they appear in `details`. The first one is canonical, the rest are typos or variants seen in prod.
  keys: string[];
  format?: FieldFormat;
  // Separator when the key repeats (default ", "); addresses use a new line because they contain commas.
  joinWith?: string;
  // Consumes the line without showing it (structural headers, data we must not show to the borrower).
  hidden?: boolean;
  // Personal data: hidden (and ssn / address formats applied) only when masking, shown as it came otherwise.
  sensitive?: boolean;
  // Skips the row when it repeats the value of an earlier field (by display label).
  hideIfSameAs?: string;
};

export type DetailRow = { label: string; value: string };

export type ParsedDetails = {
  rows: DetailRow[];
  // Joined values by the field's display label, for building titles.
  byLabel: Record<string, string>;
};

export type ParseOptions = {
  // Borrower view: hide sensitive fields and mask SSN and addresses. Internal users see everything (default true).
  maskSensitive?: boolean;
  // Name for a first line that has no label (e.g. exclusion records start with the person's name).
  leadingUnlabeledAs?: string;
};

const stripBrackets = (value: string) => {
  const match = /^\[(.*)\]$/.exec(value.trim());
  return (match ? match[1] : value).trim();
};

// Unknown keys keep their text; only ALL CAPS ones are lowercased so they read like the other labels.
const prettifyKey = (key: string) => {
  if (!/[A-Z]/.test(key) || key !== key.toUpperCase()) return key;
  const lower = key.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const isEmpty = (value: string) => value === '' || value.toLowerCase() === 'null';

// Only the last 4 digits; when those are already masked in the report (XXXX) there is nothing useful to show.
export const formatSsn = (value: string) => {
  const compact = value.replace(/[^0-9A-Za-z]/g, '');
  const lastFour = compact.slice(-4);
  return /^\d{4}$/.test(lastFour) ? `Ending in ${lastFour}` : '';
};

// The report sometimes sends empty parts as "null" ("[null, CITY, ST, ZIP]").
export const formatAddress = (value: string) =>
  value
    .split(',')
    .map(part => part.trim())
    .filter(part => part && part.toLowerCase() !== 'null')
    .join(', ');

export const formatCurrency = (value: string) => {
  if (value.includes('$') || !/^[\d,]+(\.\d+)?$/.test(value)) return value;
  const amount = Number(value.replace(/,/g, ''));
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const applyFormat = (value: string, format: FieldFormat | undefined, mask: boolean) => {
  switch (format) {
    case 'ssn':
      return mask ? formatSsn(value) : value;
    case 'address':
      return formatAddress(value);
    case 'currency':
      return formatCurrency(value);
    default:
      return value;
  }
};

type LabelEntry = { norm: string; fieldIndex: number };

const buildLabelIndex = (fields: FieldDef[]): LabelEntry[] =>
  fields
    .reduce<LabelEntry[]>(
      (entries, field, fieldIndex) => entries.concat(field.keys.map(key => ({ norm: key.trim().toLowerCase(), fieldIndex }))),
      []
    )
    .sort((a, b) => b.norm.length - a.norm.length);

// A label matches when the line starts with it and what follows is not a letter, so
// "Case Type" never swallows "Case Typed" but still matches "Disposition Date07/14/1999".
const findLabel = (line: string, index: LabelEntry[]) => {
  const lower = line.toLowerCase();
  return index.find(entry => {
    if (!lower.startsWith(entry.norm)) return false;
    const next = lower.charAt(entry.norm.length);
    return next === '' || !/[a-z]/.test(next);
  });
};

/**
 * Parses the `Key: Value\n` text stored in resolutions.details using the labels of one section.
 * It does not split on ':' because prod data mixes formats (keys without ':', glued values,
 * repeated keys, stray spaces). Lines that match no label are kept as rows with their original key.
 */
export const parseDetails = (details: string | null | undefined, fields: FieldDef[], options: ParseOptions = {}): ParsedDetails => {
  const mask = options.maskSensitive !== false;
  const index = buildLabelIndex(fields);
  const lines = (details || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const buckets: string[][] = fields.map(() => []);
  const extraRows: DetailRow[] = [];
  let leadingRow: DetailRow | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const entry = findLabel(line, index);

    if (entry) {
      let value = line.slice(entry.norm.length).replace(/^[\s:]+/, '');

      // "Address:" followed by the address on the next line.
      const nextLine = lines[i + 1];
      if (isEmpty(value) && nextLine && !nextLine.includes(':') && !findLabel(nextLine, index)) {
        value = nextLine;
        i++;
      }
      value = stripBrackets(value);
      if (!isEmpty(value)) buckets[entry.fieldIndex].push(value);
      continue;
    }

    if (i === 0 && options.leadingUnlabeledAs && !line.includes(':')) {
      leadingRow = { label: options.leadingUnlabeledAs, value: stripBrackets(line) };
      continue;
    }

    const colon = line.indexOf(':');
    const label = prettifyKey(colon > 0 ? line.slice(0, colon).trim() : line);
    const value = colon > 0 ? stripBrackets(line.slice(colon + 1)) : '';
    if (colon > 0 && isEmpty(value)) continue;
    extraRows.push({ label, value });
  }

  const rows: DetailRow[] = [];
  const byLabel: Record<string, string> = {};
  if (leadingRow) {
    rows.push(leadingRow);
    byLabel[leadingRow.label] = leadingRow.value;
  }

  fields.forEach((field, fieldIndex) => {
    if (field.hidden || (field.sensitive && mask)) return;
    const values = Array.from(new Set(buckets[fieldIndex].map(value => applyFormat(value, field.format, mask)).filter(value => !isEmpty(value))));
    if (!values.length) return;
    const value = values.join(field.joinWith ?? ', ');
    byLabel[field.label] = value;
    if (field.hideIfSameAs && byLabel[field.hideIfSameAs] === value) return;
    rows.push({ label: field.label, value });
  });

  return { rows: [...rows, ...extraRows], byLabel };
};
